import React, { useEffect, useMemo, useState } from "react";
import { Link } from "gatsby";
import JobsHero from "../HeroSection";
import { formatLocationBadge, getAllJobs } from "../../../utils/jobs";

import "./Jobs.scss";

const getHashText = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
};

const setHashFromTitle = (title) => {
  if (typeof window === "undefined") {
    return;
  }

  const pathWithQuery = `${window.location.pathname}${window.location.search}`;
  const nextHash = title ? `#${encodeURIComponent(title)}` : "";

  if (window.location.hash !== nextHash) {
    window.history.replaceState(null, "", `${pathWithQuery}${nextHash}`);
  }
};

const Jobs = () => {
  const [query, setQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("ALL");
  const [hasProcessedInitialHash, setHasProcessedInitialHash] = useState(false);

  const allJobs = useMemo(() => getAllJobs(), []);

  const locationOptions = useMemo(() => {
    const seen = new Map();

    allJobs.forEach((job) => {
      const nickname = (job.locationNickname || "").trim();
      if (!nickname) {
        return;
      }

      const key = nickname.toUpperCase();
      if (!seen.has(key)) {
        seen.set(key, nickname);
      }
    });

    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [allJobs]);

  const uniqueLocationCount = useMemo(() => {
    const normalizedLocations = allJobs
      .map((job) => (job.location || "").trim().toLowerCase())
      .filter(Boolean);

    return new Set(normalizedLocations).size;
  }, [allJobs]);

  const filteredJobs = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

    const rankedMatches = [];

    allJobs.forEach((job, index) => {
      const locationMatch =
        selectedLocation === "ALL" ||
        (job.locationNickname || "").trim().toUpperCase() === selectedLocation;

      if (!locationMatch) {
        return;
      }

      if (!terms.length) {
        rankedMatches.push({ job, score: 0, index });
        return;
      }

      const positionTitle = (job.positionTitle || "").toLowerCase();
      const searchableText = [
        positionTitle,
        job.location,
        job.locationNickname,
        job.employmentType,
        job.expectedStartDate,
        ...(job.jobDescription || []),
        ...(job.qualifications || []),
      ]
        .join(" ")
        .toLowerCase();

      const matchesAllTerms = terms.every((term) =>
        searchableText.includes(term)
      );
      if (!matchesAllTerms) {
        return;
      }

      const titleMatchCount = terms.reduce(
        (count, term) => (positionTitle.includes(term) ? count + 1 : count),
        0
      );

      rankedMatches.push({
        job,
        score: titleMatchCount,
        index,
      });
    });

    rankedMatches.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.index - b.index;
    });

    return rankedMatches.map((entry) => entry.job);
  }, [allJobs, query, selectedLocation]);

  /**
   * Groups the visible jobs under their location nickname (HEAD OFFICE, SUBIC,
   * BATANGAS, ...) so the default listing mirrors how the openings are
   * tracked internally. Group order follows the order the jobs come back in,
   * so search relevance still bubbles the closest match to the top.
   */
  const groupedJobs = useMemo(() => {
    const groups = new Map();

    filteredJobs.forEach((job) => {
      const nickname = (job.locationNickname || "").trim();
      const key = nickname.toUpperCase() || "OTHER LOCATIONS";

      if (!groups.has(key)) {
        groups.set(key, { key, name: nickname || "Other Locations", jobs: [] });
      }

      groups.get(key).jobs.push(job);
    });

    return Array.from(groups.values());
  }, [filteredJobs]);

  const [openJobId, setOpenJobId] = useState(null);

  useEffect(() => {
    if (!filteredJobs.length) {
      setOpenJobId(null);
      return;
    }

    if (openJobId === null) {
      return;
    }

    const isOpenJobVisible = filteredJobs.some((job) => job.id === openJobId);

    if (!isOpenJobVisible) {
      setOpenJobId(filteredJobs[0].id);
    }
  }, [filteredJobs, openJobId]);

  useEffect(() => {
    const hashText = getHashText();

    if (!hashText) {
      setHasProcessedInitialHash(true);
      return;
    }

    setQuery(hashText);
    setSelectedLocation("ALL");

    const normalizedHash = hashText.toLowerCase();
    const exactMatch = allJobs.find(
      (job) => job.positionTitle.toLowerCase() === normalizedHash
    );
    const partialMatch = allJobs.find((job) =>
      job.positionTitle.toLowerCase().includes(normalizedHash)
    );
    const matchedJob = exactMatch || partialMatch;

    if (matchedJob) {
      setOpenJobId(matchedJob.id);
      setHashFromTitle(matchedJob.positionTitle);
    } else {
      setOpenJobId(null);
      setHashFromTitle("");
    }

    setHasProcessedInitialHash(true);
  }, [allJobs]);

  useEffect(() => {
    if (!hasProcessedInitialHash) {
      return;
    }

    if (openJobId === null) {
      setHashFromTitle("");
      return;
    }

    const openedJob = allJobs.find((job) => job.id === openJobId);
    setHashFromTitle(openedJob ? openedJob.positionTitle : "");
  }, [allJobs, hasProcessedInitialHash, openJobId]);

  const handleJobToggle = (job, isOpen) => {
    if (isOpen) {
      setOpenJobId(null);
      return;
    }

    setOpenJobId(job.id);
  };

  const renderJobCard = (job) => {
    const isOpen = openJobId === job.id;

    return (
      <article key={job.id} className="job-card" role="listitem">
        <button
          type="button"
          className="job-card-header"
          onClick={() => handleJobToggle(job, isOpen)}
          aria-expanded={isOpen}
          aria-controls={`job-panel-${job.id}`}
        >
          <span className="title-wrap">
            <strong> {job.positionTitle}</strong>
          </span>
          <span className="meta-wrap">
            {!isOpen && (
              <span className="location-badge">
                {formatLocationBadge(job.location)}
              </span>
            )}
            <span> {job.employmentType}</span>
            <span className="chevron" aria-hidden="true">
              {isOpen ? "−" : "+"}
            </span>
          </span>
        </button>
        <div
          className={`job-card-panel-wrap ${isOpen ? "open" : "closed"}`}
          aria-hidden={!isOpen}
        >
          <div className="job-card-panel" id={`job-panel-${job.id}`}>
            <div className="job-meta">
              <p>
                Apply now as a {job.positionTitle} at{" "}
                {formatLocationBadge(job.location)}
                <br />
                <Link className="job-detail-link" to={job.path}>
                  View full page
                </Link>
                <hr />
                <strong>Location:</strong>{" "}
                {job.locationNickname
                  ? `${job.locationNickname} - ${job.location}`
                  : job.location}{" "}
                <br />
                <strong>Expected start:</strong> {job.expectedStartDate}
              </p>
            </div>
            <hr />
            <div className="job-columns">
              <div>
                <h3>Job Description</h3>
                <ul>
                  {(job.jobDescription || []).map((item, idx) => (
                    <li key={`job-${job.id}-desc-${idx}`}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>Qualifications</h3>
                <ul>
                  {(job.qualifications || []).map((item, idx) => (
                    <li key={`job-${job.id}-qual-${idx}`}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <hr />
          </div>
        </div>
      </article>
    );
  };

  return (
    <>
      <JobsHero
        totalJobs={allJobs.length}
        uniqueLocationCount={uniqueLocationCount}
      />
      <section className="careers-board">
        <div className="careers-intro">
          <p>
            Explore openings across the STAR group. Use search and filters to
            find the role that matches your experience.
          </p>
        </div>
        <div className="careers-controls">
          <div>
            <label htmlFor="job-search" className="control-label">
              Search for a role, location, skill, or keyword
            </label>
            <input
              id="job-search"
              type="search"
              placeholder="Example: procurement, engineer, Batangas"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="location-filter" className="control-label">
              Filter by location
            </label>
            <select
              id="location-filter"
              value={selectedLocation}
              onChange={(event) => setSelectedLocation(event.target.value)}
            >
              <option value="ALL">All Locations</option>
              {locationOptions.map((location) => (
                <option key={location.code} value={location.code}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="careers-results">
          Showing {filteredJobs.length} of {allJobs.length} Open Positions
        </p>
        {groupedJobs.map((group) => (
          <section className="jobs-group" key={group.key}>
            <h2 className="jobs-group-heading">
              <span className="jobs-group-name">{group.name}</span>
              <span className="jobs-group-count">
                {group.jobs.length}{" "}
                {group.jobs.length === 1 ? "position" : "positions"}
              </span>
            </h2>
            <div className="jobs-list" role="list">
              {group.jobs.map((job) => renderJobCard(job))}
            </div>
          </section>
        ))}
        {!filteredJobs.length && (
          <div className="jobs-empty-state">
            <p>No matching positions found.</p>
            <p>Try another keyword or reset the location filter.</p>
          </div>
        )}
      </section>
    </>
  );
};

export default Jobs;
